import {
  DEFAULT_WAREHOUSE_ID,
  NUMBER_OF_SWATCHES_TO_SHOW,
} from 'commons/constants';
import { hitHasInStockAtWarehouseIds } from 'components/consumer/category-page-algolia/getQualifyingHitVariants';

export const getHighlightedOptionColors = (product, items) => {
  // find the item object from Algolia that corresponds to this product
  const algoliaItem = items.find(
    item => String(item.product_id) === String(product.id)
  );

  if (algoliaItem?._highlightResult?.option_color === undefined) {
    return [];
  }

  // creates array of highlighted option colors like ["red", "blue"]
  const matchedColors = [
    ...new Set(
      algoliaItem?._highlightResult?.option_color
        ?.filter(color => color.matchLevel !== 'none')
        ?.map(highlight => highlight.value.replace(/<\/?(mark|em)>/g, '')) // remove <mark> and <em> tags
        ?.flat()
        ?.map(word => word.toLowerCase())
    ),
  ];

  return matchedColors || [];
};

export const getVariantsByMatchedColorFirst = (
  product,
  items,
  selectedColors = []
) => {
  const highlightedColors = getHighlightedOptionColors(product, items);

  // Algolia's `_highlightResult.option_color` is the source of truth for which
  // color the hit matched. On the very first paint (seeded products, before the
  // client Algolia fetch resolves) that highlight is missing, so fall back to
  // the colors the user selected via the URL/sidebar. This keeps color-filtered
  // deep links (e.g. ?color=black) showing the right swatches immediately
  // without disabling the server product seed (which would slow first paint).
  const matchedColors =
    highlightedColors.length > 0
      ? highlightedColors
      : selectedColors.map(color => color.toLowerCase());

  if (matchedColors.length === 0) {
    if (product?.variantsToShow?.length > 0) {
      return product.variantsToShow;
    }

    return product?.variants?.slice(0, NUMBER_OF_SWATCHES_TO_SHOW) ?? [];
  }

  const matchedVariants = matchedColors.flatMap(color =>
    product.variants.filter(variant => variant.color && variant.color === color)
  );

  const variantsToShow = [
    ...new Map(
      [
        ...(matchedVariants ?? []),
        ...(product?.variantsToShow ?? []),
      ].map(item => [item?.sku, item])
    ).values(),
  ];

  return variantsToShow.length > 0
    ? variantsToShow.slice(0, NUMBER_OF_SWATCHES_TO_SHOW)
    : product?.variantsToShow;
};

/**
 * One card per product. `getVariantQuickship` decides the badge per variant:
 * with a zip, plain pages badge the card if any Algolia SKU is stocked at the
 * zip warehouse or the default DC (same membership as the Ready-to-Ship
 * filter). Without a zip they use record-level `is_in_stock`.
 * SKU-matching pages badge every restricted variant (the record-level flag is
 * product-wide and warehouse-blind, so it could contradict the swatches shown).
 */
const buildProductCard = ({
  product,
  algoliaMetaData,
  cardQuickship,
  getVariantQuickship,
}) => {
  const variantsWithQuickship = (product.variantsToShow || []).map(
    variantData => ({
      ...variantData,
      quickship: getVariantQuickship(variantData),
    })
  );
  const variantsWithColor = variantsWithQuickship.filter(
    variant => !!variant?.color
  );
  // Keep at least one variant so we always show price
  const variantsToShow =
    variantsWithColor.length > 0
      ? variantsWithColor
      : variantsWithQuickship.slice(0, 1);

  return {
    ...product,
    quickship: cardQuickship,
    variants: (product.variants || []).map(variantData => ({
      ...variantData,
      quickship: getVariantQuickship(variantData),
    })),
    variantsToShow,
    algoliaMetaData,
  };
};

/**
 * Re-orders a list of enriched products to match Algolia's ranked hit order,
 * and annotates each product with quickship status and Algolia metadata.
 * Pure function — safe to call from useMemo.
 *
 * With `variantMatching` active the products arrive already filtered to their
 * matched upholstery variants — zero-match products were dropped upstream
 * (or, on a `keepUnmatchedProducts` page type, kept with full variants and an
 * `isVariantMatchingFallback` mark), so the result can fall short of
 * `items.length`. Here matching only changes where quickship comes from: a
 * restricted variant is stocked at the user's warehouse by construction (that
 * is what put it on the allowlist), so it and its card badge unconditionally;
 * only a fallback card reads any Algolia SKU at the zip or default warehouse
 * (or `is_in_stock` with no zip) like a plain category card. With the
 * Ready-to-Ship toggle on, the Algolia filter already proved that membership,
 * so the card badges without reading the hit's warehouse arrays.
 */
export const buildProductsInAlgoliaOrder = (
  products,
  items,
  indexName,
  variantMatching = null,
  { hasUserZip = false, warehouseId, isReadyToShipActive = false } = {}
) => {
  if (!items?.length || !products?.length) {
    return products ?? [];
  }

  const productsByIdMap = new Map();
  products.forEach(product => {
    const idNormalized = String(product.id);
    if (!productsByIdMap.has(idNormalized)) {
      productsByIdMap.set(idNormalized, product);
    }
  });

  return items.flatMap(hit => {
    const {
      __position,
      __queryID,
      is_in_stock,
      objectID,
      product_id: productIdFromAlgolia,
    } = hit;
    const productIdNormalized = String(productIdFromAlgolia);
    const productIdNumber = parseInt(productIdFromAlgolia, 10);
    const product = productsByIdMap.get(productIdNormalized);

    if (!product) {
      return [];
    }

    const algoliaMetaData = {
      queryID: __queryID ?? '',
      position: __position ?? 0,
      objectID: objectID ?? productIdNumber ?? '',
      indexName,
    };

    // On a SKU-matching page `filterProductsToMatchedVariants` has already
    // restricted `product.variants` to the SKUs whose warehouse array holds
    // the user's warehouse, so every variant the card can show is stocked
    // there by construction — re-deriving that from the hit's arrays could
    // only ever agree. A zero-drop fallback product is the
    // exception: it carries its full, unrestricted variant list, so it badges
    // like a plain category card, from any Algolia SKU at the zip or default
    // warehouse (or the record-level flag when there is no zip) below.
    if (variantMatching && !product.isVariantMatchingFallback) {
      return [
        buildProductCard({
          product,
          algoliaMetaData,
          cardQuickship: true,
          getVariantQuickship: () => true,
        }),
      ];
    }

    const isInStock = is_in_stock === true;
    // Toggle-on already proved zip-or-154 membership in Algolia `filters`.
    const cardQuickship = hasUserZip
      ? isReadyToShipActive ||
        hitHasInStockAtWarehouseIds(hit, [warehouseId, DEFAULT_WAREHOUSE_ID])
      : isInStock;

    return [
      buildProductCard({
        product,
        algoliaMetaData,
        cardQuickship,
        getVariantQuickship: hasUserZip
          ? variantData => variantData.quickship === true
          : () => cardQuickship,
      }),
    ];
  });
};

/**
 * Serialized key for filter + sort changes so pagination, Show All, and the
 * infinite-hits page counter reset together.
 *
 * `query` is always in the key so a new search resets Load More's page
 * counter. `warehouseId` is only in the key when it changes the Algolia
 * `filters` string (Ready-to-Ship toggle on, or a SKU-matching page type).
 * `hasUserZip` is always in the key: with a zip the Ready-to-Ship clause
 * changes, and with the toggle off Configure also retrieves
 * `product_variants.in_stock_warehouseIds` for the card badge.
 * Configure-based filters do not reset `useInfiniteHits` on their own, so a
 * ZIP/warehouse switch after Load More would otherwise keep the stale page.
 */
export const buildHitsFilterStateKey = ({
  query,
  currentProductIndex,
  refinementList,
  isReadyToShipActive,
  minWidth,
  maxWidth,
  selectedFacets,
  warehouseId,
  hasUserZip,
  variantMatching,
}) =>
  JSON.stringify({
    query: query || '',
    currentProductIndex,
    refinementList: refinementList || {},
    isReadyToShipActive,
    minWidth,
    maxWidth,
    selectedFacets: selectedFacets
      ? Object.fromEntries(
          Object.entries(selectedFacets).map(([attr, values]) => [
            attr,
            [...values].sort(),
          ])
        )
      : undefined,
    warehouseId:
      isReadyToShipActive || variantMatching ? warehouseId : undefined,
    hasUserZip: Boolean(hasUserZip),
  });

export const getColorFilterSignature = uri => {
  const decoded = decodeURIComponent(uri);
  const facetFiltersRaw = decoded
    .split('&')
    .find(param => param.startsWith('facetFilters='));
  const facetFilters = facetFiltersRaw
    ? JSON.parse(facetFiltersRaw.split('=')[1])
    : [];
  const colorFilters = facetFilters
    .flat() // Flatten the array of facet filters
    .filter(filter => filter.startsWith('option_color:'))
    .join(','); // Extract color filters and join them into a string

  return colorFilters ?? '';
};
